import PrimaryButton from '@/components/Buttons/PrimaryButton';
import AlertBanner from '@/components/common/AlertBanner';
import clsx from 'clsx';
import React from 'react';

export interface InfoSectionButton {
    text: string;
    url: string;
}

export type InfoSectionLayout = 'stacked' | 'side-button';

export interface InfoSection {
    title: string;
    html_content: string;
    buttons: InfoSectionButton[];
    layout?: InfoSectionLayout;
}

interface InfoSectionCardProps {
    section: InfoSection;
    canBook?: boolean;
}

// The 1-on-1 booking page lives inside the LMS; every other button on these cards
// points at an external provider and opens in a new tab.
const isInternalUrl = (url: string): boolean => !url.startsWith('http');

// That booking page 404s without a billing order and a live enrolment, and an Inertia
// <Link> to a 404 fails silently -- so the button is disabled rather than left looking
// clickable. Only the internal booking button is gated; an external link is not ours
// to judge.
const isBookingBlocked = (url: string, canBook: boolean): boolean =>
    isInternalUrl(url) && !canBook;

const InfoSectionCard: React.FC<InfoSectionCardProps> = ({
    section,
    canBook = true,
}) => {
    const isSideButton = section.layout === 'side-button';
    const bookingBlocked = (section.buttons ?? []).some((button) =>
        isBookingBlocked(button.url, canBook)
    );

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
            <h2 className="mb-6 text-3xl font-bold text-gray-900 dark:text-white">
                {section.title}
            </h2>
            <div
                className={clsx(
                    isSideButton && 'flex flex-col gap-6 lg:flex-row lg:items-center'
                )}
            >
                <div
                    className={clsx(
                        'text-gray-700 dark:text-neutral-300',
                        isSideButton
                            ? 'html-content flex-1 text-justify'
                            : 'info-section-content mb-6 space-y-3 [&_li]:block [&_ul]:list-none [&_ul]:space-y-3 [&_ul_ul]:mt-2 [&_ul_ul]:ml-6'
                    )}
                    dangerouslySetInnerHTML={{
                        __html: section.html_content,
                    }}
                />
                <div
                    className={clsx(
                        isSideButton
                            ? 'flex w-full flex-col gap-3 lg:w-auto lg:shrink-0'
                            : 'flex flex-col gap-3 sm:flex-row'
                    )}
                >
                    {(section.buttons ?? []).map((button, index) =>
                        isBookingBlocked(button.url, canBook) ? (
                            <PrimaryButton
                                key={`info-btn-${index}`}
                                as="button"
                                type="button"
                                disabled
                                size={isSideButton ? 'md' : 'full-width'}
                                className={clsx(
                                    'disabled:opacity-50',
                                    !isSideButton && 'tracking-wide uppercase'
                                )}
                            >
                                {button.text}
                            </PrimaryButton>
                        ) : (
                            <PrimaryButton
                                key={`info-btn-${index}`}
                                as="link"
                                href={button.url}
                                target={
                                    isInternalUrl(button.url)
                                        ? undefined
                                        : '_blank'
                                }
                                size={isSideButton ? 'md' : 'full-width'}
                                className={clsx(!isSideButton && 'tracking-wide uppercase')}
                            >
                                {button.text}
                            </PrimaryButton>
                        )
                    )}
                </div>
            </div>
            {bookingBlocked ? (
                <AlertBanner variant="amber" className="mt-4">
                    Ahora mismo no puedes reservar una sesión 1-1. Esto suele
                    deberse a que tu matrícula está pausada, ha caducado, o a
                    que tu pago aún no consta en la plataforma. Si crees que se
                    trata de un error, escríbenos y lo revisamos.
                </AlertBanner>
            ) : null}
        </div>
    );
};

export default InfoSectionCard;
